import { PATHS } from '@belong/common';
import { STEP_GATE_NAMES } from 'accounts/constants/steps';
import { SECTIONS_STRINGS } from 'accounts/constants/strings/sections.strings';
import type {
  FlowFlowModel,
  ReportModel,
  InspectionModel,
  UnitLeaseDetailsModel,
} from 'api/models';
import { isAfter } from 'date-fns';
import { kebabCase, groupBy } from 'es-toolkit/compat';
import {
  ReportStatus,
  StepStatus,
  FlowStatus,
  ReportType,
  FlowType,
} from 'models/enums';
import {
  extractAndSortStepsInGroup,
  getStepByName,
  PostInspectionFlowGroupConfig,
} from 'pages/PostInspectionFlow/step.consts';
import { getStepPathFromConfig } from 'pages/PostInspectionFlow/steps/routes';
import { formatAddressWithCity } from 'utils/formatAddress';
import { isStepUnHidden } from './flow-steps';

function getPropertyOrUnitStepsMetadata({
  stepsByGate,
  gateName,
  propertyId,
  unitId,
  includeUserSteps = false,
}: {
  stepsByGate: any;
  gateName: string;
  propertyId: string;
  unitId: string;
  includeUserSteps?: boolean;
}) {
  const propertySteps =
    groupBy(stepsByGate[gateName] ?? {}, (step) => (step as any)?.dataRelId)?.[
      propertyId
    ] || [];

  const unitSteps =
    groupBy(stepsByGate[gateName] ?? {}, (step) => (step as any)?.dataRelId)?.[
      unitId
    ] || [];
  let userSteps = [];

  /**
   * This is a hack to start including the User Profile step as part of the Build Listing section.
   * It will be removed shortly once the Action Items page is implemented.
   */
  if (includeUserSteps) {
    const flowId = (unitSteps[0] as Record<string, unknown>)?.flowId as string;

    userSteps =
      groupBy(
        stepsByGate[gateName] ?? {},
        (step) => (step as any)?.dataType,
      )?.User?.filter(
        (userStep) => (userStep as Record<string, unknown>)?.flowId === flowId,
      ) || [];
  }

  const steps = [...propertySteps, ...unitSteps, ...userSteps];
  const noHiddenSteps = steps.filter(isStepUnHidden);
  const completedSteps =
    noHiddenSteps.filter((step) => step.status === StepStatus.Completed) || [];
  const progressPercentage = Math.round(
    (completedSteps.length * 100) / noHiddenSteps.length || 0,
  );

  return { steps: noHiddenSteps, completedSteps, progressPercentage };
}

function getReportStepsMetadata({
  report,
  reportType,
  postInspectionSteps,
}: {
  report: any;
  reportType: FlowFlowModel['flowType'];
  postInspectionSteps: any;
}) {
  const isUnpublished = report?.status === ReportStatus.Unpublished;
  const isCompleted = report?.status === ReportStatus.Completed;
  const isConfirmedOrCompleted = report?.isConfirmed || isCompleted;

  // `reportType` is always `Improvements` or `Pricing`, and the config declares a
  // group for each, so this always finds one.
  const { key } = PostInspectionFlowGroupConfig.find(
    (groupConfig) => groupConfig.name === reportType,
  )!;

  const reportSteps = extractAndSortStepsInGroup(postInspectionSteps, key);

  const noHiddenSteps = postInspectionSteps?.filter(isStepUnHidden) || [];
  const completedSteps =
    noHiddenSteps.filter((step: any) => step.status === StepStatus.Completed) ||
    [];
  const progressPercentage = Math.round(
    (completedSteps.length * 100) / noHiddenSteps.length || 0,
  );

  const firstNonCompletedStep =
    noHiddenSteps.find((step: any) => step.status !== 'Completed') ||
    reportSteps[0];

  // We replace the id since the paths are based on the uniqueId
  const link = reportSteps.length
    ? getStepPathFromConfig({
        ...firstNonCompletedStep,
        id: firstNonCompletedStep.uniqueId,
      })
    : null;

  return {
    isUnpublished,
    isConfirmedOrCompleted,
    progressPercentage,
    link,
  };
}

type GetLandlordInsuranceSectionParams = {
  flows: FlowFlowModel[];
  unitId: string;
};

function getUnitLandlordInsuranceSectionMetadata({
  flows,
  unitId,
}: GetLandlordInsuranceSectionParams) {
  const unitLandlordInsuranceFlow = flows.filter((flow) =>
    flow.configuration?.unitIds?.some((id: string) => id === unitId),
  )?.[0];

  if (typeof unitLandlordInsuranceFlow === 'undefined') {
    return null;
  }

  const unitLandlordInsuranceSectionMetadata = {
    name: STEP_GATE_NAMES.LandlordInsurance,
    displayName: SECTIONS_STRINGS.add_insurance,
    reportLink: `${PATHS.LANDLORD_INSURANCE_FLOW}?flowId=${unitLandlordInsuranceFlow.uniqueId}`,
  };

  if (unitLandlordInsuranceFlow?.status === 'Completed') {
    return {
      ...unitLandlordInsuranceSectionMetadata,
      progressPercentage: 100,
      isCompleted: true,
      disabled: true,
    };
  }

  // `steps` is optional in the model but a landlord-insurance flow always carries
  // them; an absent list was already a crash on `.length` rather than a zero.
  const allSteps = unitLandlordInsuranceFlow.steps!.length;
  const completedSteps = unitLandlordInsuranceFlow.steps!.filter(
    (step) => step.status === 'Completed',
  ).length;
  const progressPercentage = Math.round((completedSteps / allSteps) * 100);

  return {
    ...unitLandlordInsuranceSectionMetadata,
    progressPercentage,
    isCompleted: false,
    disabled: false,
  };
}

function getPostInspectionFlowDisplayName(
  flowType: FlowFlowModel['flowType'],
  flowSubType: string,
) {
  if (flowType === ReportType.Improvements) {
    switch (flowSubType) {
      case 'Onboarding':
        return SECTIONS_STRINGS['home_improvements.displayName'];
      case 'MoveOut':
        return SECTIONS_STRINGS['home_improvements_pre_move_out.displayName'];
      case 'PreMoveOut':
        return SECTIONS_STRINGS['home_improvements_move_out.displayName'];
      case 'QA':
        return SECTIONS_STRINGS['home_readiness_walkthrough.displayName'];
      default:
        return null;
    }
  }

  switch (flowSubType) {
    // A walkthrough's pricing flow still sets a rent price, so it takes the
    // same label as onboarding's.
    case 'Onboarding':
    case 'QA':
      return SECTIONS_STRINGS['home_pricing.displayName'];
    case 'MoveOut':
    case 'PreMoveOut':
      return SECTIONS_STRINGS['home_pricing_move_out.displayName'];
    default:
      return null;
  }
}

type ReportMetadataByTypeProps = {
  // Both callers pass the result of a `reduce` that starts at `null`, and every
  // read below already goes through `flow?.`. `reportId` is likewise the optional
  // `dataUniqueId` of a step that may not exist, and is only ever compared.
  flow: FlowFlowModel | null;
  flowType: FlowFlowModel['flowType'];
  flowSubType: string;
  reports: ReportModel[];
  reportId?: ReportModel['uniqueId'];
};

function getReportMetadataByType({
  flow,
  flowType,
  flowSubType,
  reports,
  reportId,
}: ReportMetadataByTypeProps) {
  const sections = [];

  const {
    progressPercentage,
    isUnpublished: isReportUnpublished,
    isConfirmedOrCompleted: isReportCompletedOrConfirmed,
    link: reportLink,
  } = getReportStepsMetadata({
    report: reports.find((report) => report?.uniqueId === reportId),
    reportType: flowType,
    postInspectionSteps:
      flow?.steps?.map((step) => ({
        ...step,
        ...getStepByName(step.stepName),
      })) || [],
  });

  sections.push({
    name:
      flowType === ReportType.Improvements
        ? STEP_GATE_NAMES.HomeImprovements
        : STEP_GATE_NAMES.HomePricing,
    displayName: getPostInspectionFlowDisplayName(flowType, flowSubType),
    progressPercentage,
    isCompleted:
      flowType === ReportType.Improvements
        ? flow?.status === 'Completed'
        : isReportCompletedOrConfirmed,
    disabled:
      isReportUnpublished ||
      (flowType === ReportType.Pricing &&
        isReportCompletedOrConfirmed &&
        flow?.status === 'Completed') || // Should only disable when report confirmed on improvements
      !reportLink ||
      progressPercentage >= 100,
    reportLink,
  });

  return sections;
}

type ReportSectionMedatadaProps = {
  flows: FlowFlowModel[];
  unitId: string;
  // `InspectionModel`, not the `InspectionDetailModel` TypeScript suggests: the
  // reads below are `status`, `type`, `scheduledOn` and `createdOn`, all of which
  // are on `InspectionModel`, and none of which `InspectionDetailModel` has.
  inspections: InspectionModel[];
  reports: ReportModel[];
};

function getReportSectionMetadata({
  flows,
  unitId,
  inspections,
  reports,
}: ReportSectionMedatadaProps) {
  const unitFlows = flows.filter(
    (flow) => flow.configuration?.homeUniqueId === unitId,
  );
  const sections = [];

  // Find latest inspection
  let latestInspection: InspectionModel | null | undefined = null;

  latestInspection = inspections?.find(
    (inspection) => inspection.status === 'Pending',
  );

  if (!latestInspection) {
    latestInspection = inspections?.reduce<InspectionModel | null>(
      (prev, current) => {
        if (!prev) {
          return current;
        }

        // `scheduledOn` is optional, and `new Date(NaN)` is the same Invalid Date
        // `new Date(undefined)` produced here before — which `isAfter` reports as
        // `false` either way.
        return isAfter(
          new Date(current.scheduledOn ?? NaN),
          new Date(prev.scheduledOn ?? NaN),
        )
          ? current
          : prev;
      },
      null,
    );
  }

  // Find latest pricing flow based on latest inspection
  // The type argument is what makes `prev` the accumulator's real type; inferred
  // from the `null` seed alone it is `null`, so every read off `prev` below is an
  // error. `new Date(0)` is the same instant as the `new Date(null)` this used to
  // evaluate, for every falsy `createdOn`.
  const pricingFlow = unitFlows.reduce<FlowFlowModel | null>(
    (prev, current) => {
      if (
        current.flowType === ReportType.Pricing &&
        current.configuration?.flowSubType === latestInspection?.type &&
        isAfter(
          new Date(current.auditInfo.createdOn),
          new Date(latestInspection?.createdOn || 0),
        )
      ) {
        if (!prev) {
          return current;
        }

        return isAfter(
          new Date(current.auditInfo.createdOn),
          new Date(prev.auditInfo.createdOn),
        )
          ? current
          : prev;
      }

      return prev;
    },
    null,
  );

  const pricingReportId = pricingFlow?.steps?.find(
    (step) => step.dataType === 'Report',
  )?.dataUniqueId;

  // A walkthrough (`QA`) inspection does not currently produce a Pricing flow,
  // which would push a linkless, disabled section. `pricingFlow` is already
  // scoped to this inspection's subtype, so it is the whole test: if a QA
  // pricing flow ever does appear, render it rather than dropping it silently.
  if (latestInspection?.type !== 'QA' || pricingFlow) {
    sections.push(
      ...getReportMetadataByType({
        flow: pricingFlow,
        flowType: 'Pricing',
        flowSubType: latestInspection?.type || 'Onboarding',
        reports,
        reportId: pricingReportId,
      }),
    );
  }

  // Find latest improvement flow based on latest inspection
  const improvementFlow = unitFlows.reduce<FlowFlowModel | null>(
    (prev, current) => {
      if (
        current.flowType === ReportType.Improvements &&
        current.configuration?.flowSubType === latestInspection?.type &&
        isAfter(
          new Date(current.auditInfo.createdOn),
          new Date(latestInspection?.createdOn || 0),
        )
      ) {
        if (!prev) {
          return current;
        }

        return isAfter(
          new Date(current.auditInfo.createdOn),
          new Date(prev.auditInfo.createdOn),
        )
          ? current
          : prev;
      }

      return prev;
    },
    null,
  );

  const improvementReportId = improvementFlow?.steps?.find(
    (step) => step.dataType === 'Report',
  )?.dataUniqueId;

  sections.push(
    ...getReportMetadataByType({
      flow: improvementFlow,
      flowType: 'Improvements',
      flowSubType: latestInspection?.type || 'Onboarding',
      reports,
      reportId: improvementReportId,
    }),
  );

  return sections;
}

function getPreMoveOutSectionMetadata({
  flows,
  leaseId,
}: {
  flows: any;
  leaseId: string;
}) {
  const preMoveOutFlow = flows
    .filter(
      (flow: any) =>
        flow.flowType === 'HomeownerPreMoveOut' &&
        flow.configuration?.leaseUniqueId === leaseId,
    )
    .sort((a: any, b: any) =>
      isAfter(new Date(a.auditInfo.createdOn), new Date(b.auditInfo.createdOn))
        ? -1
        : 1,
    )[0];

  if (preMoveOutFlow) {
    const { uniqueId: flowId, steps } = preMoveOutFlow;
    const firstNonCompletedStep = steps
      ?.filter((step: any) => !step.isHidden)
      .find((step: any) => step.status !== 'Completed');
    const { uniqueId: stepId, stepName } = firstNonCompletedStep || {};

    const noHiddenSteps = steps.filter(isStepUnHidden);
    const completedSteps =
      noHiddenSteps.filter(
        (step: any) => step.status === StepStatus.Completed,
      ) || [];
    const progressPercentage = Math.round(
      (completedSteps.length * 100) / noHiddenSteps.length || 0,
    );

    return {
      name: STEP_GATE_NAMES.PreMoveOut,
      displayName: SECTIONS_STRINGS['pre_move_out.displayName'],
      progressPercentage,
      isCompleted: progressPercentage === 100,
      disabled: progressPercentage === 100,
      reportLink: `${PATHS.HO_PRE_MOVE_OUT_FLOW}?flowId=${flowId}&stepId=${stepId}&stepName=${kebabCase(
        stepName ?? '',
      )}`,
    };
  }

  return null;
}

export function getPropertySection({
  stepsByGate,
  selectedProperty,
  reports,
  flows,
  inspections,
  units,
}: {
  stepsByGate: any;
  selectedProperty: any;
  reports: any;
  flows: any;
  inspections: any;
  units: any;
}) {
  /**
   * Now we can send agreements for homes that are part of a multi-home onboarding flow without waiting
   * for every single one of them to be agreement-ready.
   * The "units" param only considers homes that have an active agreement.
   * Therefore, we are filtering property units based on the units that are part of an agreement.
   */
  const unitIds =
    (units as UnitLeaseDetailsModel[])?.map((unit) => unit.basicInfo!.unitId) ??
    [];

  const propertySection = selectedProperty?.units
    .map((unit: any) => {
      const unitSubSections = [];
      const isAdoptedResident = unit.isAdoptedAgreement;

      if (!unitIds.includes(unit.basicInfo.unitId)) {
        return;
      }

      const inspectionScheduleContentProps = {
        subtitle: SECTIONS_STRINGS['what_you_need.sub_title'],
        items: isAdoptedResident
          ? [
              { text: SECTIONS_STRINGS.current_lease },
              { text: SECTIONS_STRINGS.resident_contact_info },
            ]
          : [],
      };

      const {
        progressPercentage: inspectionSchedulePercentage,
        steps: inspectionScheduleSteps,
      } = getPropertyOrUnitStepsMetadata({
        stepsByGate,
        gateName: STEP_GATE_NAMES.InspectionSchedule,
        propertyId: selectedProperty?.basicInfo.propertyId,
        unitId: unit?.basicInfo.unitId,
      });

      const {
        progressPercentage: buildListingPercentage,
        steps: buildListingSteps,
      } = getPropertyOrUnitStepsMetadata({
        stepsByGate,
        gateName: STEP_GATE_NAMES.BuildListing,
        propertyId: selectedProperty?.basicInfo.propertyId,
        unitId: unit?.basicInfo.unitId,
        includeUserSteps: true,
      });

      unitSubSections.push({
        name: STEP_GATE_NAMES.InspectionSchedule,
        displayName: isAdoptedResident
          ? SECTIONS_STRINGS['inspection_schedule.adopted_resident.displayName']
          : SECTIONS_STRINGS['inspection_schedule.displayName'],
        progressPercentage: inspectionSchedulePercentage,
        isCompleted: inspectionSchedulePercentage === 100,
        steps: inspectionScheduleSteps,
        additionalContentProps: {
          ...(selectedProperty?.basicInfo.hasHoa && {
            title: SECTIONS_STRINGS.hoa_agreement_and_info,
          }),
          ...(inspectionScheduleContentProps.items?.length &&
            inspectionScheduleContentProps),
        },
        disabled: inspectionSchedulePercentage === 100,
      });

      unitSubSections.push({
        name: STEP_GATE_NAMES.BuildListing,
        displayName: isAdoptedResident
          ? SECTIONS_STRINGS['build_listing.adopted_resident.displayName']
          : SECTIONS_STRINGS['build_listing.displayName'],
        progressPercentage: buildListingPercentage,
        isCompleted: buildListingPercentage === 100,
        steps: buildListingSteps,
        disabled: buildListingSteps?.every((step) =>
          [FlowStatus.Cancelled, FlowStatus.Completed].includes(step.status),
        ),
      });

      const unitlandlordInsuranceFlowMetadata =
        getUnitLandlordInsuranceSectionMetadata({
          flows,
          unitId: unit.basicInfo.unitId,
        });

      if (unitlandlordInsuranceFlowMetadata) {
        unitSubSections.push(unitlandlordInsuranceFlowMetadata);
      }

      const preMoveOutFlowMetadata = getPreMoveOutSectionMetadata({
        flows,
        leaseId: unit.currentLease?.basicInfo.leaseId,
      });

      if (preMoveOutFlowMetadata) unitSubSections.push(preMoveOutFlowMetadata);

      const unitInspections = inspections?.filter(
        (inspection: any) => inspection.homeUniqueId === unit.basicInfo.unitId,
      );

      if (
        !isAdoptedResident ||
        (isAdoptedResident && unitInspections?.length > 0)
      ) {
        const unitReports = reports
          .filter(
            (reportMetadata: any) =>
              reportMetadata.report.homeId === unit?.basicInfo.unitId,
          )
          ?.map((reportMetadata: any) => reportMetadata.report);

        const reportsMetadata = getReportSectionMetadata({
          flows,
          inspections: unitInspections,
          unitId: unit.basicInfo.unitId,
          reports: unitReports,
        });

        unitSubSections.push(...reportsMetadata);
      }

      return {
        sectionTitle: formatAddressWithCity(
          selectedProperty?.address,
          unit.basicInfo.unitNumber,
        ),
        subSections: unitSubSections,
        unitId: unit.basicInfo.unitId,
        timeZone: unit.basicInfo.timeZone,
        isCompleted: unitSubSections.every(
          (subSection) => subSection.isCompleted,
        ),
      };
    })
    .filter(Boolean);

  return propertySection;
}

function getAccountStepsMetadata({
  stepsByGate,
  gateName,
  property,
}: {
  stepsByGate: any;
  gateName: string;
  property: any;
}) {
  const steps = stepsByGate[gateName]?.filter((step: any) =>
    step.dataType === 'Home'
      ? property?.units.some(
          (unit: any) => unit.basicInfo.unitId === step.dataUniqueId,
        )
      : true,
  );
  const noHiddenSteps = steps.filter(isStepUnHidden);
  const completedSteps = noHiddenSteps.filter(
    (step: any) => step.status === StepStatus.Completed,
  );
  const progressPercentage = Math.round(
    (completedSteps.length * 100) / noHiddenSteps.length,
  );

  return { steps: noHiddenSteps, completedSteps, progressPercentage };
}

export function getUserSection({
  stepsByGate,
  property,
  flows,
}: {
  stepsByGate: any;
  property: any;
  flows: any;
}) {
  const subSections = [];

  if (stepsByGate[STEP_GATE_NAMES.NextLevelProfile]?.length) {
    const {
      progressPercentage: nextLevelProfilePercentage,
      steps: nextLevelProfileSteps,
    } = getAccountStepsMetadata({
      stepsByGate,
      gateName: STEP_GATE_NAMES.NextLevelProfile,
      property,
    });

    subSections.push({
      name: STEP_GATE_NAMES.NextLevelProfile,
      displayName: SECTIONS_STRINGS['next_level_profile.displayName'],
      progressPercentage: nextLevelProfilePercentage,
      isCompleted: nextLevelProfilePercentage === 100,
      steps: nextLevelProfileSteps,
      disabled: nextLevelProfileSteps?.every((step: any) =>
        [FlowStatus.Cancelled, FlowStatus.Completed].includes(step.flowStatus),
      ),
    });
  }

  if (stepsByGate[STEP_GATE_NAMES.GetPaid]?.length) {
    const { progressPercentage: getPaidPercentage, steps: getPaidSteps } =
      getAccountStepsMetadata({
        stepsByGate,
        gateName: STEP_GATE_NAMES.GetPaid,
        property,
      });

    subSections.push({
      name: STEP_GATE_NAMES.GetPaid,
      displayName: SECTIONS_STRINGS['get_paid.displayName'],
      progressPercentage: getPaidPercentage,
      isCompleted: getPaidPercentage === 100,
      steps: getPaidSteps,
      disabled: getPaidSteps?.every((step: any) =>
        [FlowStatus.Cancelled, FlowStatus.Completed].includes(step.flowStatus),
      ),
      additionalContentProps: {
        subtitle: SECTIONS_STRINGS['what_you_need.sub_title'],
        items: [
          { text: SECTIONS_STRINGS.tax_info },
          { text: SECTIONS_STRINGS.insurance },
          { text: SECTIONS_STRINGS.valid_id },
        ],
      },
    });
  }

  const w9Flow = flows?.find((flow: any) => flow.flowType === FlowType.W9);
  if (w9Flow) {
    const isW9Completed = w9Flow?.status === 'Completed';
    subSections.push({
      name: STEP_GATE_NAMES.W9,
      displayName: SECTIONS_STRINGS.w9,
      reportLink: PATHS.W9_FLOW.replace(':flowId', w9Flow.uniqueId),
      progressPercentage: isW9Completed ? 100 : 0,
      isCompleted: isW9Completed,
      disabled: isW9Completed,
    });
  }

  const userSection = {
    sectionTitle: SECTIONS_STRINGS['account.title'],
    subSections,
    isCompleted: subSections.every((subSection) => subSection.isCompleted),
  };

  return userSection;
}
